import {Component, computed, EventEmitter, OnInit, Output, signal, AfterViewInit, OnDestroy} from '@angular/core';
import {forkJoin} from "rxjs";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {ActivatedRoute, Router} from "@angular/router";
import {CustomMessageService, ResponseHttpError, ResponseHttpSuccess, UtilService} from "library";
import {RequestCancelReason, SalesWorkflowSteps, SalesWorkflow} from "projects/store/src/app/enums";
import {LocalStorageKey} from "projects/store/src/app/enums";
import {LocalStorageService} from "library";
import {CategoryRequestService} from "projects/store/src/app/services/api/v1/category-request.service";
import {PathNavigationService} from 'projects/store/src/app/services/api/v1/path-navigation.service';
import {environment} from "projects/store/src/environments/environment";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ErrorTemplate} from "projects/store/src/app/interfaces";
import {AuthService} from "projects/store/src/app/services/api/v1/auth.service";
import {PixelService} from "projects/store/src/app/services/api/v1/pixel.service";

@Component({
  selector: 'app-sales-credit-summary-kredicash',
  templateUrl: './sales-credit-summary-kredicash.component.html',
})
export class SalesCreditSummaryKredicashComponent implements OnInit, AfterViewInit, OnDestroy {

  requestId: string;
  selectedProduct: any;
  selectedPrice!: number;
  selectedDay!: number;
  country = environment.global.country;
  visibleModalChangeQuota: boolean = false;
  visibleModalApprovedLess: boolean = false;
  form!: FormGroup;
  error!: ErrorTemplate;
  flowCode: string;

  request = signal<any | undefined>(undefined);
  products = signal<any>([]);
  prices = signal<any>([]);
  minPrice = signal<number>(1000000000);
  maxPrice = signal<number>(0);
  minDay = signal<number>(15);
  maxDay = signal<number>(0);
  dayOptions: any[] = [];
  isSubmitted = signal<boolean>(false);
  simulationError406 = false;
  isRequestApproved = false;

  isBetterProduct = computed(() => this.request()?.best_financeable_product?.quotes.prices.suggested > this.request()?.simulation.amount);

  isApprovedEqual = computed(() =>
    this.request()?.quotes.prices.selected === this.request()?.simulation.amount
    && this.request()?.quotes.quota.quantity === this.request()?.simulation.quota_quantity
    && this.request()?.quotes.quota.selected === this.request()?.simulation.quota_from
  );

  isApprovedLess = computed(() =>
    this.request()?.quotes.prices.selected < this.request()?.simulation.amount
    || this.request()?.quotes.quota.quantity !== this.request()?.simulation.quota_quantity
    || this.request()?.quotes.quota.selected !== this.request()?.simulation.quota_from
  );

  returnPaymentDate = signal<string>('');

  @Output() nextStepEvent = new EventEmitter();
  @Output() gotoStepEvent = new EventEmitter();

  constructor(
    public requestService: RequestService,
    protected categoryRequestService: CategoryRequestService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected utilService: UtilService,
    protected localStorageService: LocalStorageService,
    protected customMessageService: CustomMessageService,
    protected pathNavigationService: PathNavigationService,
    protected metaPixelService: PixelService,
    protected authService: AuthService
  ) {
    this.requestId = this.route.snapshot.params['request_id'];
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.form = new FormGroup({
      salary: new FormControl(null, Validators.required),
    });
    this.requestService.step.set(5);
  }

  /**
   * Determina si el flujo actual es Krdya (V3)
   */
  get isFlowKrdya(): boolean {
    return this.flowCode === SalesWorkflow['app-krdya-kredicash'];
  }

  ngOnInit() {
    // Container: la inicialización se delega a los hijos (V2/V3)
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.requestService.step.set(5);
    }, 0);
  }

  ngOnDestroy() {
    this.requestService.step.set(0);
  }

  load() {
    this.utilService.enablePageLoading();

    // Para el flujo kredicash-cliente, limpiar la simulación del localStorage
    if (!this.isFlowKrdya) {
      this.localStorageService.remove(LocalStorageKey.simulation);
    }

    forkJoin({
      request: this.requestService.get(this.requestId),
      products: this.requestService.getProductsByCategory(this.requestId, this.requestService.categoryId),
    }).subscribe({
      next: (resp) => {
        if (resp.request?.status === HttpStatusCode.Ok) {
          this.request.set(resp.request.body.data);
          this.calculateReturnPaymentDate();
          this.metaPixelService.trackEvent('AddToCart');
        } else {
          this.customMessageService.responseNoticeHandler(resp.request);
        }

        if (resp.products?.status === HttpStatusCode.Ok) {
          if (resp.products.body.data.list.length === 0) {
            this.cancelRequest();
            this.gotoStepEvent.emit(SalesWorkflowSteps['not-approved-request']);
          } else {
            this.products.set(resp.products.body.data.list);
            this.onExtractMinMaxPrices();
            this.form.patchValue({
              salary: this.request().simulation?.salary
            });
            if (this.isApprovedLess()) {
              this.visibleModalApprovedLess = true;
            }
            this.isRequestApproved = true;
            this.utilService.disablePageLoading();
          }
        }else{
          this.customMessageService.responseNoticeHandler(resp.products);
          this.utilService.disablePageLoading();
        }
      },
      error: (error) => {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      },
    });
  }

  validate() {
    this.requestService.validateSimulation(this.requestId, this.request()?.simulation).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        const selected_product = resp.body.data.selected_product;
        selected_product.quotes.prices.selected = selected_product.quotes.prices.suggested;
        selected_product.quotes.quota.selected = selected_product.quotes.quota.from;
        selected_product.quotes.installment.selected = selected_product.quotes.installment.from;
        const best_financeable_product = resp.body.data.best_financeable_product;
        const data = {
          product: {id: selected_product.id, code: selected_product.code, name: selected_product.name},
          best_financeable_product: best_financeable_product,
          quotes: selected_product.quotes,
        };
        this.updateRequest(data);
      },
      error: (error: ResponseHttpError) => {
        if (error.status == HttpStatusCode.NotAcceptable) {
          this.simulationError406 = true;
          this.cancelRequest();
        } else {
          this.customMessageService.comunicationError();
          this.utilService.disablePageLoading();
        }
      }
    });
  }

  updateRequest(data: any) {
    this.requestService.update(this.requestId, data).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status == HttpStatusCode.NoContent) {
          this.load();
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }

        this.visibleModalChangeQuota = false;
        this.requestService.step.set(5);
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.Locked) {
          this.handleRequestLocked(err as any);
        }else{
          this.customMessageService.responseNoticeHandler(err);
          this.utilService.disablePageLoading();
        }
      }
    });
  }

  getRequest() {
    this.utilService.enablePageLoading();
    this.requestService.get(this.requestId).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status == HttpStatusCode.Ok) {
          this.request.set(resp.body.data);
          this.validate();
        }else{
          this.customMessageService.responseNoticeHandler(resp);
          this.utilService.disablePageLoading();
        }
      },
      error: (err: HttpErrorResponse)=>{
        this.customMessageService.responseNoticeHandler(err);
        this.utilService.disablePageLoading();
      }
    });
  }

  onExtractMinMaxPrices() {
    let availableDays: number[] = [];
    this.products().forEach((row: any) => {
      row.quotes.forEach((row1: any) => {
        this.minPrice.set(row1.prices.suggested < this.minPrice() ? row1.prices.suggested : this.minPrice());
        this.maxPrice.set(row1.prices.suggested > this.maxPrice() ? row1.prices.suggested : this.maxPrice());
        const currentDay = row1.quota.quantity * 15;
        availableDays.push(currentDay);

        if (!this.prices().includes(row1.prices.suggested)) {
          this.prices.update((prices: any) => [...prices, row1.prices.suggested]);
        }

        if (row1.prices.suggested == this.maxPrice()) {
          this.selectedProduct = row;
        }
      });
    });
    const uniqueDays = availableDays
      .filter((item, index) => availableDays.indexOf(item) === index)
      .sort((a, b) => Number(a) - Number(b));
    this.minDay.set(uniqueDays[0]);
    this.maxDay.set(uniqueDays[uniqueDays.length - 1]);
    this.selectedPrice = this.maxPrice();
    this.selectedDay = this.maxDay();
    this.onExtractDaysForSelected();
  }

  onExtractDaysForSelected() {
    const min = this.minDay();
    const max = this.maxDay();

    this.dayOptions = [];

    for (let i = min; i <= max; i += 60) {
      this.dayOptions.push(i);
    }
  }

  onSlideEnd(price: any) {
    if (!this.prices().includes(price)) {
      let previousPrice = this.selectedProduct.quotes[0].prices.suggested;
      let priceTemp: any;
      if (price < previousPrice) {
        this.prices().sort((a: any, b: any) => b - a).forEach((row: any) => {
          if (row < price && priceTemp == undefined) {
            priceTemp = row;
          }
        });
      } else {
        this.prices().sort((a: any, b: any) => a - b).forEach((row: any) => {
          if (row > price && priceTemp == undefined) {
            priceTemp = row;
          }
        });
      }

      if (priceTemp) {
        this.selectedPrice = priceTemp;
      }
    }
    this.selectedProduct = this.getProductByPrice(this.selectedPrice);
  }

  getProductByPrice(price: number) {
    let product = null;
    this.products().forEach((row: any) => {
      row.quotes.forEach((row1: any) => {
        if (row1.prices.suggested === price) {
          row.quotes.sort((a: any, b: any) => a.quota.quantity - b.quota.quantity);
          product = row;
        }
      });
    });
    return product;
  }

  calculateReturnPaymentDate() {
    let date = new Date();
    const frequency = this.request()?.quotes.frecuency || 0;
    date.setDate(date.getDate() + frequency);
    this.returnPaymentDate.set(date.toISOString());
  }

  onModalSave(data: any) {
    this.utilService.enablePageLoading();
    const requestData = {
      product: data.product,
      quotes: data.quotes
    };
    this.updateRequest(requestData);
  }

  updateCredit() {
    this.utilService.enablePageLoading();
    const selectedQuota = this.selectedDay / this.request().simulation.frecuency;
    const data = {
      product: {id: this.selectedProduct.id, code: this.selectedProduct.code, name: this.selectedProduct.name},
      quotes: this.selectedProduct.quotes.filter((row: any) => row.prices.suggested == this.selectedPrice && row.quota.quantity == selectedQuota)[0]
    };
    data.quotes.frecuency = 15;
    data.quotes.quota.selected = data.quotes.quota.from;
    data.quotes.prices.selected = data.quotes.prices.suggested;
    this.updateRequest(data);
  }

  cancelRequest() {
    const reasonText = RequestCancelReason.NO_PRODUCTS_AVAILABLE;
    this.metaPixelService.trackEvent('RejectedApplication');
    this.requestService.cancel(this.requestId, reasonText).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.NoContent) {
          this.handleRequestNotApproved(reasonText);
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }
      },
      error: (err: HttpErrorResponse)=>{
        this.customMessageService.responseNoticeHandler(err);
      }
    });
  }

  showModal() {
    this.visibleModalChangeQuota = true;
  }

  approveRequest() {
    this.requestService.approved(this.requestId).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status === HttpStatusCode.NoContent) {
          this.onNextStep();
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }
      },
      error: (err: HttpErrorResponse)=>{
        this.customMessageService.responseNoticeHandler(err);
      }
    });
  }

  onNextStep() {
    this.utilService.enablePageLoading();

    // Para el flujo app-krdya-kredicash, limpiar la simulación del localStorage
    // al avanzar al siguiente paso
    if (this.isFlowKrdya) {
      this.localStorageService.remove(LocalStorageKey.simulation);
    }

    this.nextStepEvent.emit();
  }

  onGotoStep(stepCode: string) {
    this.gotoStepEvent.emit(stepCode);
  }

  get f() {
    return this.form.controls;
  }

  protected handleRequestLocked(resp: any): void {
    const messageError = resp.error?.message ?? 'Solicitud no disponible';
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = {attemptedUrl, messageError};

    this.pathNavigationService.navigateToRequestNotAvailable(this.flowCode, state);
  }

  protected handleRequestNotApproved(messageError: string): void {
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = {attemptedUrl, messageError};

    this.pathNavigationService.navigateToRequestNotApproved(this.flowCode, state);
  }

  protected readonly SalesWorkflowSteps = SalesWorkflowSteps;
}
